import React from "react";
import { Footprints, Maximize2, Plus, BoxSelect, MousePointerClick, Eraser, Sparkles, ChevronDown, Info } from "lucide-react";
import ScopeSelector from "./ScopeSelector";
import QuickActionsMenu from "./QuickActionsMenu";
import SelectedSpacesDrawer from "./SelectedSpacesDrawer";
import { SearchMode, DistanceType } from "../types/drawerTypes";

interface TopControlBarProps {
  searchMode: SearchMode;
  distanceType: DistanceType;
  selectedPlanDetail: any;
  selectedPlan: any;
  showScopeMenu: boolean;
  showDistanceMenu: boolean;
  onToggleScopeMenu: () => void;
  onToggleDistanceMenu: () => void;
  onScopeChange: (mode: SearchMode) => void;
  onDistanceTypeChange: (type: DistanceType) => void;
  onAddSelectedSpaces: () => void;
  onDragSelectSpaces: () => void;
  onSelectSpaceForAI: () => void;
  onClearHighlight: () => void;
  isDragSelectActive?: boolean;
  isSelectingSpaceForAI?: boolean;
  showQuickActionsMenu?: boolean;
  onToggleQuickActionsMenu?: () => void;
  onTriggerQuickAction?: (actionName: string) => void;
  quickActionsConfig?: any;
  selectedSpacesChips?: string[];
  onRemoveSpaceChip?: (spaceId: string) => void;
  onClearAllSpaceChips?: () => void;
  plansInfo?: any;
}

const TopControlBar: React.FC<TopControlBarProps> = ({
  searchMode,
  distanceType,
  selectedPlanDetail,
  selectedPlan,
  showScopeMenu,
  showDistanceMenu,
  onToggleScopeMenu,
  onToggleDistanceMenu,
  onScopeChange,
  onDistanceTypeChange,
  onAddSelectedSpaces,
  onDragSelectSpaces,
  onSelectSpaceForAI,
  onClearHighlight,
  isDragSelectActive = false,
  isSelectingSpaceForAI = false,
  showQuickActionsMenu = false,
  onToggleQuickActionsMenu,
  onTriggerQuickAction,
  quickActionsConfig,
  selectedSpacesChips,
  onRemoveSpaceChip,
  onClearAllSpaceChips,
  plansInfo,
}) => {
  return (
    <div className="flex flex-col border-b border-slate-100">
      {/* CSS for blinking animations */}
      <style>{`
        @keyframes redBlink {
          0%, 100% { background-color: rgba(239, 68, 68, 0.1); box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5); }
          50% { background-color: rgba(239, 68, 68, 0.3); box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3); }
        }
        @keyframes tealBlink {
          0%, 100% { background-color: rgba(20, 184, 166, 0.1); box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.5); }
          50% { background-color: rgba(20, 184, 166, 0.3); box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.3); }
        }
        .drag-select-active {
          animation: redBlink 1s ease-in-out infinite;
          color: rgb(239, 68, 68) !important;
        }
        .space-select-active {
          animation: tealBlink 1s ease-in-out infinite;
          color: rgb(20, 184, 166) !important;
        }
      `}</style>
      
      {/* Helpful Hint Banner */}
      <div className="bg-slate-50/80 text-slate-500 px-3 py-1 text-[10.5px] flex items-center gap-1.5 border-b border-slate-100 w-full">
        <Info size={12} className="text-indigo-400" />
        <span><strong className="font-semibold text-slate-600">Tip:</strong> Use <strong>Shift+Click</strong> on the canvas to select, multi-select or deselect spaces.</span>
      </div>

      <div className="flex flex-wrap items-center px-3 py-1.5 text-xs gap-1">
          <ScopeSelector
            searchMode={searchMode}
            selectedPlanDetail={selectedPlanDetail}
            selectedPlan={selectedPlan}
            showScopeMenu={showScopeMenu}
            onToggleMenu={onToggleScopeMenu}
            onScopeChange={onScopeChange}
          />

          {onToggleQuickActionsMenu && (
            <div className="relative">
              <button
                onClick={onToggleQuickActionsMenu}
                className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 font-medium transition py-1 px-2 rounded-md hover:bg-slate-50"
              >
                <Sparkles size={14} className="text-indigo-500" />
                <span className="whitespace-nowrap">Quick Actions</span>
                <ChevronDown size={12} />
              </button>
              {onTriggerQuickAction && quickActionsConfig && (
                <QuickActionsMenu
                  showQuickActionsMenu={showQuickActionsMenu}
                  onTriggerAction={onTriggerQuickAction}
                  onClose={onToggleQuickActionsMenu}
                  actionsConfig={quickActionsConfig}
                  hasSelectedSpaces={!!selectedSpacesChips && selectedSpacesChips.length > 0}
                  selectedSpacesCount={selectedSpacesChips?.length || 0}
                />
              )}
            </div>
          )}

          {selectedSpacesChips && onRemoveSpaceChip && onClearAllSpaceChips && (
            <SelectedSpacesDrawer
              selectedSpacesChips={selectedSpacesChips}
              onRemoveSpaceChip={onRemoveSpaceChip}
              onClearAllSpaceChips={onClearAllSpaceChips}
              plansInfo={plansInfo}
            />
          )}

          <button
            onClick={onDragSelectSpaces}
            className={`flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 font-medium transition py-1 px-2 rounded-md hover:bg-slate-50 ${isDragSelectActive ? 'drag-select-active' : ''}`}
          >
            <BoxSelect size={14} className="text-orange-500" />
            <span className="whitespace-nowrap">
              Drag Select
            </span>
          </button>

          {/* Deprecated Add Spaces Button
          <button
            onClick={onAddSelectedSpaces}
            className="group relative flex items-center justify-center bg-transparent hover:bg-purple-50 text-slate-400 hover:text-purple-600 transition-all duration-200 p-1.5 rounded-md"
          >
            <Plus size={16} />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 px-2 py-1 text-[10px] font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Add Spaces
            </span>
          </button>
          */}

          <button
            onClick={() => {
              onClearHighlight();
              if (onClearAllSpaceChips) {
                onClearAllSpaceChips();
              }
            }}
            className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 font-medium transition py-1 px-2 rounded-md hover:bg-slate-50"
          >
            <Eraser size={14} className="text-rose-500" />
            <span className="whitespace-nowrap">
              Clear Spaces
            </span>
          </button>

          <div className="h-4 w-px bg-slate-200"></div>

          <div className="relative">
            <button
              onClick={onToggleDistanceMenu}
              className={`group relative flex items-center justify-center text-slate-600 hover:text-indigo-600 transition p-1.5 rounded-md hover:bg-slate-50 ${showDistanceMenu ? "bg-slate-100 text-slate-800" : ""}`}
            >
              {distanceType === "direct" ? (
                <Maximize2 size={16} className="text-blue-500" />
              ) : (
                <Footprints size={16} className="text-blue-500" />
              )}
              <span className="absolute bottom-full right-0 mb-0.5 px-2 py-1 text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {distanceType === "direct" ? "Direct Distance" : "Walking Distance"}
              </span>
            </button>

            {showDistanceMenu && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={onToggleDistanceMenu}
                ></div>
                <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-slate-100 py-1 z-40 animate-in fade-in zoom-in-95 duration-100">
                  <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Measure Mode
                  </div>
                  <button
                    onClick={() => onDistanceTypeChange("walking")}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 transition ${distanceType === "walking"
                      ? "text-blue-600 font-semibold bg-blue-50/50"
                      : "text-slate-600"
                      }`}
                  >
                    <Footprints size={14} /> Walking Distance
                  </button>
                  <button
                    onClick={() => onDistanceTypeChange("direct")}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 transition ${distanceType === "direct"
                      ? "text-blue-600 font-semibold bg-blue-50/50"
                      : "text-slate-600"
                      }`}
                  >
                    <Maximize2 size={14} /> Direct Distance
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Deprecated Select Space Button
          <button
            onClick={onSelectSpaceForAI}
            className={`group relative flex items-center justify-center bg-transparent hover:bg-teal-50 text-slate-400 hover:text-teal-600 transition-all duration-200 p-1.5 rounded-md ${isSelectingSpaceForAI ? 'space-select-active' : ''}`}
          >
            <MousePointerClick size={16} />
            <span className="absolute bottom-full right-0 mb-0.5 px-2 py-1 text-[10px] font-semibold text-teal-600 bg-teal-50 border border-teal-200 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Select Space
            </span>
          </button>
          */}
      </div>
    </div>
  );
};

export default TopControlBar;
